import React, { useMemo } from 'react';
import { getTeamMeta } from './aflTeams';

// Sleeveless AFL guernsey silhouette (viewBox 0 0 100 104). Patterns are
// clipped to this path so every club reads as a jersey, not a swatch.
const JERSEY_PATH =
    'M40 8 C44 15 56 15 60 8 L74 8 L92 22 L80 40 L73 33 L73 98 L27 98 L27 33 L20 40 L8 22 L26 8 Z';

// Per-club guernsey recipes. `base` is the body colour; `layers` paint the
// club's pattern on top (stripes / hoops / sash / chevron / centre panel).
// Keyed by the canonical full club name (lowercased) used across aflTeams.
const GUERNSEYS = {
    'adelaide crows': { base: '#041e42', collar: '#ffd200', layers: [{ type: 'centerV', colors: ['#ffd200', '#e21937', '#ffd200'], width: 34 }] },
    'brisbane lions': { base: '#6c0023', collar: '#f6b500', layers: [{ type: 'sash', color: '#f6b500', dir: '\\', width: 28 }, { type: 'sash', color: '#0a3a8f', dir: '\\', width: 9 }] },
    'carlton blues': { base: '#0a1e44', collar: '#ffffff', layers: [] },
    'collingwood magpies': { base: '#0b0b0b', collar: '#ffffff', layers: [{ type: 'stripesV', colors: ['#0b0b0b', '#f5f5f5'], count: 7 }] },
    'essendon bombers': { base: '#161616', collar: '#d4111e', layers: [{ type: 'sash', color: '#d4111e', dir: '\\', width: 26 }] },
    'fremantle dockers': { base: '#2a0a52', collar: '#ffffff', layers: [{ type: 'chevron', colors: ['#ffffff'], top: 4, depth: 22, thickness: 6 }] },
    'geelong cats': { base: '#0a1d3f', collar: '#ffffff', layers: [{ type: 'stripesH', colors: ['#0a1d3f', '#ffffff'], count: 7 }] },
    'gold coast suns': { base: '#d4202a', collar: '#ffd200', layers: [{ type: 'centerV', colors: ['#ffd200'], width: 18 }, { type: 'sash', color: '#0a3a8f', dir: '/', width: 9 }] },
    'gws giants': { base: '#2b3138', collar: '#f47920', layers: [{ type: 'sash', color: '#f47920', dir: '/', width: 26 }] },
    'hawthorn hawks': { base: '#4a2c12', collar: '#f5b800', layers: [{ type: 'stripesV', colors: ['#4a2c12', '#f5b800'], count: 7 }] },
    'melbourne demons': { base: '#0a1a3f', collar: '#d4111e', layers: [{ type: 'chevron', colors: ['#d4111e'], top: 4, depth: 28, thickness: 15 }] },
    'north melbourne kangaroos': { base: '#0a2a8c', collar: '#ffffff', layers: [{ type: 'stripesV', colors: ['#0a2a8c', '#ffffff'], count: 7 }] },
    'port adelaide power': { base: '#0b0b0b', collar: '#08aebe', layers: [{ type: 'stripesV', colors: ['#0b0b0b', '#ffffff'], count: 9 }, { type: 'centerV', colors: ['#08aebe'], width: 8 }] },
    'richmond tigers': { base: '#161616', collar: '#ffd200', layers: [{ type: 'sash', color: '#ffd200', dir: '\\', width: 26 }] },
    'st kilda saints': { base: '#ffffff', collar: '#ed1c24', layers: [{ type: 'chevron', colors: ['#ed1c24', '#0a0a0a'], top: 4, depth: 28, thickness: 12 }] },
    'sydney swans': { base: '#ffffff', collar: '#e1251b', layers: [{ type: 'chevron', colors: ['#e1251b'], top: 6, depth: 30, thickness: 19 }] },
    'west coast eagles': { base: '#062e6f', collar: '#f2a900', layers: [{ type: 'chevron', colors: ['#f2a900'], top: 6, depth: 26, thickness: 9 }] },
    'western bulldogs': { base: '#14439e', collar: '#ffffff', layers: [{ type: 'stripesH', colors: ['#14439e', '#ffffff', '#d4111e'], count: 3 }] },
};

function renderLayer(layer, i) {
    switch (layer.type) {
        case 'stripesV': {
            const { colors, count } = layer;
            const w = 100 / count;
            return Array.from({ length: count }, (_, n) => (
                <rect key={`${i}-${n}`} x={n * w} y={-6} width={w + 0.4} height={116} fill={colors[n % colors.length]} />
            ));
        }
        case 'stripesH': {
            const { colors, count } = layer;
            const h = 104 / count;
            return Array.from({ length: count }, (_, n) => (
                <rect key={`${i}-${n}`} x={-6} y={n * h} width={112} height={h + 0.4} fill={colors[n % colors.length]} />
            ));
        }
        case 'centerV': {
            const { colors, width } = layer;
            const x0 = 50 - width / 2;
            const w = width / colors.length;
            return colors.map((c, n) => (
                <rect key={`${i}-${n}`} x={x0 + n * w} y={-6} width={w + 0.4} height={116} fill={c} />
            ));
        }
        case 'sash': {
            const rot = layer.dir === '/' ? -36 : 36;
            return (
                <rect
                    key={i}
                    x={-40} y={50 - layer.width / 2} width={180} height={layer.width}
                    fill={layer.color}
                    transform={`rotate(${rot} 50 52)`}
                />
            );
        }
        case 'chevron': {
            const { colors, top = 6, depth = 28, thickness = 14 } = layer;
            return colors.map((c, n) => {
                const t = top + n * thickness;
                const pts = `0,${t} 50,${t + depth} 100,${t} 100,${t + thickness} 50,${t + depth + thickness} 0,${t + thickness}`;
                return <polygon key={`${i}-${n}`} points={pts} fill={c} />;
            });
        }
        default:
            return null;
    }
}

function getGuernsey(teamName) {
    const meta = getTeamMeta(teamName);
    const key = (meta.full || teamName || '').trim().toLowerCase();
    if (GUERNSEYS[key]) return GUERNSEYS[key];
    // Unknown / future club → a clean two-tone using its brand colours.
    return { base: meta.from, collar: meta.to, layers: [{ type: 'chevron', colors: [meta.to], top: 6, depth: 26, thickness: 12 }] };
}

function TeamGuernsey({ name, className = '' }) {
    const config = getGuernsey(name);
    const clipId = useMemo(() => `gsy-${Math.random().toString(36).slice(2, 9)}`, []);

    return (
        <svg viewBox="0 0 100 104" className={className} role="img" aria-label={`${name} guernsey`}>
            <defs>
                <clipPath id={clipId}>
                    <path d={JERSEY_PATH} />
                </clipPath>
                <linearGradient id={`${clipId}-sh`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.16" />
                    <stop offset="45%" stopColor="#ffffff" stopOpacity="0" />
                    <stop offset="100%" stopColor="#000000" stopOpacity="0.22" />
                </linearGradient>
            </defs>

            <g clipPath={`url(#${clipId})`}>
                <rect x={-6} y={-6} width={112} height={116} fill={config.base} />
                {config.layers.map((layer, i) => renderLayer(layer, i))}
                {/* Fabric depth */}
                <rect x={-6} y={-6} width={112} height={116} fill={`url(#${clipId}-sh)`} />
            </g>

            {/* Collar accent + silhouette outline */}
            <path d="M40 8 C44 15 56 15 60 8" fill="none" stroke={config.collar} strokeWidth="3.5" strokeLinecap="round" />
            <path d={JERSEY_PATH} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="2" strokeLinejoin="round" />
        </svg>
    );
}

export default TeamGuernsey;
